import React from 'react';
import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS } from 'constants/query-keys';
import { GOOGLE_ANALYTICS_FUNCTIONAL_COOKIE_SLUG } from 'helpers/ConsentHelpers';
import { getConsentCookieDefinitions } from 'services/ConsentService';

import type {
  IConsentCookieDefinition,
  IConsentCookieDefinitionResponse,
  IConsentCookieDefinitionsResponse,
  ReactQueryOptions,
} from 'types';

// Hooks

export function useConsentCookieDefinitions<S = IConsentCookieDefinition[]>(
  options: ReactQueryOptions<IConsentCookieDefinitionsResponse, S> = {}
) {
  const select = React.useCallback((data: IConsentCookieDefinitionsResponse) => adapter(data, options?.select), [options.select]);
  return useQuery({
    queryKey: [QUERY_KEYS.CONSENT_COOKIE_DEFINITIONS],
    queryFn: () => getConsentCookieDefinitions(),
    staleTime: 5 * 60 * 1000,
    ...options,
    select
  });
}

// Adapter

function adapter<S = IConsentCookieDefinition[]>(
  data: IConsentCookieDefinitionsResponse,
  select?: (data: IConsentCookieDefinitionsResponse) => S
): S | IConsentCookieDefinition[] {
  if (!select) {
    return toConsentCookieDefinitions(data);
  }
  return select(data);
}

// Helpers

export function toConsentCookieDefinitions(data: IConsentCookieDefinitionsResponse): IConsentCookieDefinition[] {
  const {
    cookies = []
  } = data || {};

  // TODO: temporary hack, should be removed
  return cookies
    .filter(({ slug }) => slug !== GOOGLE_ANALYTICS_FUNCTIONAL_COOKIE_SLUG)
    .map(toConsentCookieDefinition);
}

export function toConsentCookieDefinition(data: IConsentCookieDefinitionResponse): IConsentCookieDefinition {
  const {
    id, slug, label, name, type, scope, description
  } = data || {};

  return {
    id, slug, label, name, type, scope, description
  };
}
